import * as React from "react";
import { formatDate, formatDateTime } from "@repo/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Separator,
  useToast,
} from "@repo/ui";
import { Paperclip } from "@phosphor-icons/react";
import {
  deleteReceiptInvoice,
  getReceiptInvoiceUrl,
  uploadReceiptInvoice,
} from "@/lib/api/receipts";
import {
  INVOICE_ACCEPT,
  formatFileSize,
  validateInvoiceFile,
} from "@/lib/validation/invoice-file";
import type { Receipt, ReceiptWithWholesaler } from "@/lib/types/inventory";

export interface ReceiptDetailViewProps {
  receipt: Receipt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReceiptDetailView({
  receipt,
  open,
  onOpenChange,
}: ReceiptDetailViewProps) {
  const { toast } = useToast();
  const [docMeta, setDocMeta] = React.useState<{
    name: string | null;
    size: number | null;
  } | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [removed, setRemoved] = React.useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Reset the local attach/replace/remove override whenever a different
  // receipt is shown.
  React.useEffect(() => {
    setDocMeta(null);
    setRemoved(false);
  }, [receipt?.id]);

  if (!receipt) return null;

  const fileName = removed
    ? null
    : (docMeta?.name ?? receipt.invoice_file_name ?? null);
  const fileSize = removed
    ? null
    : (docMeta?.size ?? receipt.invoice_file_size ?? null);

  const handleView = async () => {
    setIsBusy(true);
    try {
      const res = await getReceiptInvoiceUrl(receipt.id);
      if (res.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to open the invoice document.",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const error = validateInvoiceFile(file);
    if (error) {
      toast({
        title: "Invalid File",
        description: error,
        variant: "destructive",
      });
      return;
    }
    setIsBusy(true);
    try {
      await uploadReceiptInvoice(receipt.id, file);
      setDocMeta({ name: file.name, size: file.size });
      setRemoved(false);
      toast({
        title: "Document Attached",
        description: "Invoice document saved to this receipt.",
        variant: "success",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to upload the invoice document.",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemove = async () => {
    setIsBusy(true);
    try {
      await deleteReceiptInvoice(receipt.id);
      setDocMeta(null);
      setRemoved(true);
      toast({
        title: "Document Removed",
        description: "Invoice document deleted from this receipt.",
        variant: "success",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove the invoice document.",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="receipt-detail-dialog">
        <DialogHeader>
          <DialogTitle>Receipt Details</DialogTitle>
          <DialogDescription>
            Complete information for receipt {receipt.invoice_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Date Received
              </p>
              <p className="text-base font-semibold">
                {formatDate(receipt.date_received, "PPP")}
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Product Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  NDC Code
                </p>
                <p className="text-base font-mono">{receipt.ndc}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Item Name
                </p>
                <p className="text-base font-semibold">
                  {receipt.inventory?.item_name || "Unknown Item"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Quantity Received
                </p>
                <p className="text-base font-semibold">
                  {receipt.quantity_received} units
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Source Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Wholesaler
                </p>
                <p className="text-base">
                  {(() => {
                    const extendedReceipt = receipt as ReceiptWithWholesaler;
                    // Handle new format (wholesaler object from join)
                    if (
                      receipt.wholesaler &&
                      typeof receipt.wholesaler === "object" &&
                      "name" in receipt.wholesaler
                    ) {
                      return receipt.wholesaler.name;
                    }
                    // Handle wholesaler_name field
                    if (extendedReceipt.wholesaler_name) {
                      return extendedReceipt.wholesaler_name;
                    }
                    // Handle legacy format (wholesaler string)
                    if (
                      receipt.wholesaler &&
                      typeof receipt.wholesaler === "string"
                    ) {
                      return receipt.wholesaler;
                    }
                    return "—";
                  })()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Invoice Number
                </p>
                <p className="text-base font-mono">{receipt.invoice_number}</p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Invoice Document</h4>
            {fileName ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-surface px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm">{fileName}</span>
                  {fileSize != null && (
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {formatFileSize(fileSize)}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleView}
                    disabled={isBusy}
                    data-testid="receipt-invoice-view"
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isBusy}
                    data-testid="receipt-invoice-replace"
                  >
                    Replace
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmRemoveOpen(true)}
                    disabled={isBusy}
                    data-testid="receipt-invoice-remove"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
                data-testid="receipt-invoice-attach"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach Document
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={INVOICE_ACCEPT}
              className="hidden"
              onChange={handleFileSelected}
            />
            <AlertDialog
              open={confirmRemoveOpen}
              onOpenChange={setConfirmRemoveOpen}
            >
              <AlertDialogContent data-testid="receipt-invoice-remove-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove invoice document?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes the attached invoice document from
                    this receipt. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isBusy}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleRemove}
                    disabled={isBusy}
                    data-testid="receipt-invoice-remove-confirm-action"
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Audit Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Created At
                </p>
                <p className="text-sm">{formatDateTime(receipt.created_at)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Created By
                </p>
                <p className="text-sm">
                  {receipt.created_by_user?.full_name || receipt.created_by}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
